// src/algorithms/ordered/_runner.ts
import frag from '../shaders/ordered.frag?raw';
import {
  FULLSCREEN_VERT, linkProgram, createFullscreenQuad,
} from '../../core/gl';
import type { Palette } from '../../core/types';

export interface OrderedProgram {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  u: {
    src: WebGLUniformLocation | null;
    threshold: WebGLUniformLocation | null;
    palette: WebGLUniformLocation | null;
    srcSize: WebGLUniformLocation | null;
    thresholdSize: WebGLUniformLocation | null;
    paletteSize: WebGLUniformLocation | null;
    scale: WebGLUniformLocation | null;
  };
}

export function createOrderedProgram(gl: WebGL2RenderingContext): OrderedProgram {
  const program = linkProgram(gl, FULLSCREEN_VERT, frag);
  const vao = createFullscreenQuad(gl);
  return {
    program, vao,
    u: {
      src:           gl.getUniformLocation(program, 'u_src'),
      threshold:     gl.getUniformLocation(program, 'u_threshold'),
      palette:       gl.getUniformLocation(program, 'u_palette'),
      srcSize:       gl.getUniformLocation(program, 'u_srcSize'),
      thresholdSize: gl.getUniformLocation(program, 'u_thresholdSize'),
      paletteSize:   gl.getUniformLocation(program, 'u_paletteSize'),
      scale:         gl.getUniformLocation(program, 'u_scale'),
    },
  };
}

export function uploadPalette(
  gl: WebGL2RenderingContext, palette: Palette,
): { tex: WebGLTexture; size: number } {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const data = new Uint8Array(palette.colors.length * 4);
  palette.colors.forEach((c, i) => {
    data[i*4] = c.r; data[i*4+1] = c.g; data[i*4+2] = c.b; data[i*4+3] = 255;
  });
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, palette.colors.length, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return { tex, size: palette.colors.length };
}
